// Pure helpers used by both the React hooks (use-metrics.js) and the
// singleton store (metrics-store.js). Lives in its own module to keep
// the store free of React imports and to break the circular dep
// hooks → store → hooks would otherwise create.

// HLC-encoded txn IDs. The server packs `(ms_since_epoch << 16) | counter`
// into u64. Values reach ~1.16e17 in 2026, so anything above this threshold
// is HLC; below it is a fresh-cluster logical counter.
export const HLC_THRESHOLD = 1e13;
export const isHlc = n => typeof n === 'number' && Number.isFinite(n) && n > HLC_THRESHOLD;
export const hlcToMs = hlc => Math.floor(hlc / 65536);

// Render an HLC value as relative wall-clock time ("2.1s ago" / "just now").
export function fmtHlcRelative(hlc) {
  if (!isHlc(hlc)) return null;
  const ms = hlcToMs(hlc);
  const ago = Date.now() - ms;
  if (ago < 0) return 'in the future';
  if (ago < 1000) return 'just now';
  if (ago < 60_000) return `${(ago / 1000).toFixed(1)}s ago`;
  if (ago < 3_600_000) return `${Math.floor(ago / 60_000)}m ago`;
  return new Date(ms).toISOString().slice(11, 19);
}

// Render an HLC *delta* (e.g. txn_lag) as a duration. The delta is also in
// HLC units, so divide by 65536 to get ms.
export function fmtHlcDuration(hlcDelta) {
  if (hlcDelta == null || !Number.isFinite(hlcDelta)) return '—';
  const ms = hlcDelta / 65536;
  if (Math.abs(ms) < 1)         return '< 1 ms';
  if (Math.abs(ms) < 1000)      return `${ms.toFixed(0)} ms`;
  if (Math.abs(ms) < 60_000)    return `${(ms / 1000).toFixed(2)} s`;
  if (Math.abs(ms) < 3_600_000) return `${(ms / 60_000).toFixed(1)} min`;
  return `${(ms / 3_600_000).toFixed(1)} h`;
}

// HLC delta → milliseconds (for charts).
export const hlcDeltaToMs = d => (Number.isFinite(d) ? d / 65536 : null);

// Convert [[k, v], ...] rows from SHOW METRICS into a flat {key: number} map.
// Floats arrive as strings (e.g. "106.15"); coerce to number.
export function parseMetrics(rows) {
  const m = {};
  for (const [k, v] of rows) {
    m[k] = typeof v === 'number' ? v : parseFloat(v);
  }
  return m;
}

// Derive reads/sec and writes/sec from two consecutive metric samples.
// Returns null if either sample is missing or dt <= 0.
//   reads  = dml.queries_total − calvin_dml.routed_total
//   writes = calvin_dml.routed_total
// Calvin is the only write path a server-mode node has (B297 Phase 5), so its
// routed count IS the write count.
// (io.cache_* counters never move on the Calvin path, so they can't power reads.)
export function deriveRates(latest, prev, sampledAt, prevSampledAt) {
  if (!latest || !prev) return null;
  const dt = (sampledAt - prevSampledAt) / 1000;
  if (dt <= 0) return null;
  const dQueries = (latest['dml.queries_total']        || 0) - (prev['dml.queries_total']        || 0);
  const dCalvin  = (latest['calvin_dml.routed_total']  || 0) - (prev['calvin_dml.routed_total']  || 0);
  const writes = dCalvin / dt;
  const reads  = Math.max(0, dQueries - dCalvin) / dt;
  return { readsPerSec: reads, writesPerSec: Math.max(0, writes), dt };
}
